import React from 'react';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@cotera/client/v0/components/ui/toggle-group';
import { FilterChipScroller } from './filter-chip-scroller';

/** One chip in the band. */
export type FilterChipOption = { value: string; label: string };

/**
 * The control band above a list: `All`, a divider, then one chip per lens.
 *
 * The Library filters by type and Automations by trigger source, and past the
 * labels the two were the same forty lines — same roving-focus configuration,
 * same widths, same divider, same scroller — kept in step by hand. They are one
 * control with two option lists, so they are one component.
 *
 * `All` is drawn here rather than passed in, because it is not one of the
 * options: the chips after the divider each narrow the list to a single kind,
 * and `All` is the absence of that. The divider is what says so.
 *
 * Below `sm` this band gives way to {@link ListFilterMenu} — a phone has no
 * room for a row of chips and cannot afford the row it would sit in.
 */
export const FilterChipGroup: React.FC<{
  value: string;
  onValueChange: (value: string) => void;
  /** The chips after the divider. `All` is always first and is not listed. */
  options: readonly FilterChipOption[];
  /** Names the whole band, e.g. "Filter library by type". */
  ariaLabel: string;
}> = ({ value, onValueChange, options, ariaLabel }) => (
  <FilterChipScroller>
    <ToggleGroup
      type="single"
      variant="outline"
      size="sm"
      spacing={1}
      // Horizontal roving: Left/Right move across the chips, leaving Up/Down to
      // bridge between the list and this header row (see ResourceListView).
      orientation="horizontal"
      value={value}
      onValueChange={onValueChange}
      className="w-max max-w-none flex-nowrap justify-start"
      aria-label={ariaLabel}
    >
      <ToggleGroupItem value="all" className="flex-none px-3 whitespace-nowrap">
        All
      </ToggleGroupItem>
      <div
        aria-hidden="true"
        className="mx-2 h-6 w-px shrink-0 self-center bg-muted-foreground/30"
      />
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          className="flex-none px-3 whitespace-nowrap"
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  </FilterChipScroller>
);
